import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USE_REAL_API } from '@/services/api-client';
import * as noticesApi from '@/services/notices-api';

export type NoticeCategory = 'all' | 'important' | 'general' | 'hr' | 'safety';

/**
 * title/body/date are pre-formatted display strings (not i18n keys). Real
 * backend announcements are user-authored content — translating makes no
 * sense. Mocks carry the same format for consistency.
 */
export interface NoticeItem {
  id: string;
  category: Exclude<NoticeCategory, 'all'>;
  title: string;
  body: string;
  date: string;
  authorName: string;
  authorInitials: string;
  attachmentCount: number;
  unread: boolean;
}

const MOCK: NoticeItem[] = [
  {
    id: 'N-1',
    category: 'important',
    title: '生产排程变更通知',
    body: 'Q2 生产排程有调整,请各部门主管于今日 18:00 前确认。',
    date: 'Today',
    authorName: 'Zhong Chunhui',
    authorInitials: 'ZC',
    attachmentCount: 2,
    unread: true,
  },
  {
    id: 'N-2',
    category: 'hr',
    title: '五一假期安排',
    body: '4月30日至5月3日放假,5月4日正常上班。',
    date: 'Today',
    authorName: 'HR Dept.',
    authorInitials: 'HR',
    attachmentCount: 0,
    unread: true,
  },
  {
    id: 'N-3',
    category: 'safety',
    title: '车间安全检查结果',
    body: '本月安全检查已完成,整改项已下发到各班组。',
    date: 'Yesterday',
    authorName: 'Admin',
    authorInitials: 'AD',
    attachmentCount: 1,
    unread: false,
  },
];

export function useNotices(category: NoticeCategory) {
  return useQuery({
    queryKey: ['notices', category, USE_REAL_API],
    queryFn: async (): Promise<NoticeItem[]> => {
      const all = USE_REAL_API ? await noticesApi.fetchAnnouncements() : MOCK;
      if (!USE_REAL_API) await new Promise((r) => setTimeout(r, 80));
      return category === 'all' ? all : all.filter((n) => n.category === category);
    },
    staleTime: 30_000,
  });
}

export function useMarkNoticeRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (USE_REAL_API) {
        await noticesApi.markAnnouncementRead(id);
      } else {
        const item = MOCK.find((n) => n.id === id);
        if (item) item.unread = false;
      }
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notices'] });
    },
  });
}
